// Considered: RelationshipFilterMolecule (the list) — rejected as a base for this
// extraction because it owns list chrome (header/collapse/add-row/remove) that a
// single-relationship value editor (hosted inside FilterEditorPanelMolecule) must
// not duplicate. This IS the list's row body, lifted out (`expandedIndex`/
// `pickerRowIndex` move from RelationshipFilterMolecule's list-level state to this
// component's own local state — one editor instance, one relationship).
// RelationshipFilterMolecule now consumes this component for each of its rows,
// mirroring the ArcFilterMolecule → ArcValueEditorMolecule precedent (EAINT-702).
// See spec lane-in-group-editor.md Phase 2 (SC3) Reuse Audit.
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'localization';
import { isFilterVariable } from '@center-platform/spheres/filters';
import type { RelationshipFilter } from 'types/filters';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import { InputNumberAtom } from '../../atoms/InputNumber/InputNumberAtom';
import SphereSelectDialogOrganism from '../../organisms/marketplace/SphereSelectDialogOrganism';
import type { SphereSearchResult } from '../../../hooks/useSphereSearch';
import SphereRefChipMolecule from '../SphereRefChip/SphereRefChipMolecule';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import './RelationshipValueEditorMolecule.scss';

export interface RelationshipValueEditorMoleculeProps {
  /** Available relationships for this filter context (schema-derived, deduped). */
  availableRelationships: RelationshipOption[];
  /** The single `RelationshipFilter` this editor instance edits (one row = one relationship). */
  value: RelationshipFilter;
  onChange: (next: RelationshipFilter) => void;
  /** Omit to hide the remove affordance — the host (e.g. FilterEditorPanel) may provide its own. */
  onRemove?: () => void;
  readOnly?: boolean;
  /**
   * Locks the relationship (edge) select only — a measure row presents a
   * `RelationshipFilter` on the fixed `measures_into` edge and must not let
   * the author repoint it to a different relationship. Operator, direction,
   * min-count and `relatedTo` (the aggregate roster) stay editable; locking
   * the whole editor would regress that. Every existing caller omits this.
   */
  lockRelationship?: boolean;
  /**
   * Hides the direction control while the direction is `both` (the default the filter
   * carries when it has none). At `both` the control asserts nothing — the condition
   * matches either end — so it is a third control competing for a narrow row's width
   * while saying only "unset". `source`/`target` still render it, because then it IS
   * carrying the condition's meaning. A host that wants the author to be able to
   * NARROW direction leaves this off. Defaults to `false`, today's always-visible
   * control.
   */
  hideDirectionWhenBoth?: boolean;
  /** DOM index for `data-testid` parity with list hosts (e.g. `RelationshipFilterMolecule`). Default 0. */
  index?: number;
  className?: string;
}

const RelationshipValueEditorMolecule: React.FC<RelationshipValueEditorMoleculeProps> = ({
  availableRelationships,
  value: filter,
  onChange,
  onRemove,
  readOnly = false,
  lockRelationship = false,
  hideDirectionWhenBoth = false,
  index = 0,
  className = '',
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const relationship = availableRelationships.find((r) => r.systemName === filter.relationshipSystemName);
  const showMinCount = filter.operator === 'in';
  // `both` is also what an unset direction reads as — see `hideDirectionWhenBoth`.
  const showDirection = !hideDirectionWhenBoth || (filter.direction ?? 'both') !== 'both';

  const update = useCallback(
    (partial: Partial<RelationshipFilter>) => onChange({ ...filter, ...partial }),
    [filter, onChange]
  );

  const addSphere = useCallback(
    (sphere: SphereSearchResult): boolean => {
      if (filter.relatedTo.includes(sphere.id)) return false;
      update({ relatedTo: [...filter.relatedTo, sphere.id] });
      return true;
    },
    [filter, update]
  );

  const addMe = useCallback(() => {
    if (filter.relatedTo.includes('@me')) return;
    update({ relatedTo: [...filter.relatedTo, '@me'] });
  }, [filter, update]);

  const removeSphere = useCallback(
    (id: string) => update({ relatedTo: filter.relatedTo.filter((r) => r !== id) }),
    [filter, update]
  );

  return (
    <div
      className={`relationship-value-editor-molecule ${className}`.trim()}
      data-testid={`relationship-value-editor-${index}`}
    >
      <div className="relationship-value-editor-molecule__row-main">
        <DropdownAtom
          value={filter.relationshipSystemName || null}
          options={availableRelationships.map((r) => ({ label: r.label, value: r.systemName }))}
          onChange={(e) => !readOnly && !lockRelationship && update({ relationshipSystemName: e.value ?? '' })}
          disabled={readOnly || lockRelationship}
          filter
          className="relationship-value-editor-molecule__relationship-select"
          ariaLabel={t('context.editor.relationshipFilters.relationshipLabel')}
          data-testid={`relationship-select-${index}`}
        />
        <SegmentedControlAtom
          options={[
            { value: 'in', label: t('context.editor.relationshipFilters.is') },
            { value: 'not_in', label: t('context.editor.relationshipFilters.isNot') },
          ]}
          value={filter.operator}
          onChange={(v) => !readOnly && update({ operator: v as 'in' | 'not_in' })}
          mode="single"
          size="small"
          ariaLabel={t('context.editor.relationshipFilters.is')}
        />
        {showDirection && (!expanded ? (
          <button
            type="button"
            className="relationship-value-editor-molecule__advanced-toggle"
            onClick={() => setExpanded(true)}
            data-testid={`relationship-expand-${index}`}
          >
            <i className="pi pi-sliders-h" aria-hidden="true" />
            {t(`context.editor.relationshipFilters.direction.${filter.direction ?? 'both'}`)}
          </button>
        ) : (
          <SegmentedControlAtom
            options={[
              { value: 'source', label: t('context.editor.relationshipFilters.direction.source') },
              { value: 'target', label: t('context.editor.relationshipFilters.direction.target') },
              { value: 'both', label: t('context.editor.relationshipFilters.direction.both') },
            ]}
            value={filter.direction ?? 'both'}
            onChange={(v) => !readOnly && update({ direction: v as 'source' | 'target' | 'both' })}
            mode="single"
            size="small"
            ariaLabel={t('context.editor.relationshipFilters.directionLabel')}
          />
        ))}
        {!readOnly && onRemove && (
          <button
            type="button"
            className="relationship-value-editor-molecule__remove-btn"
            aria-label={t('context.editor.relationshipFilters.removeFilter')}
            data-testid={`relationship-remove-${index}`}
            onClick={onRemove}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="relationship-value-editor-molecule__related-to">
        {filter.relatedTo.length === 0 ? (
          <span className="relationship-value-editor-molecule__any-sphere">
            {t('context.editor.relationshipFilters.anySphere')}
          </span>
        ) : (
          filter.relatedTo.map((id) => (
            <SphereRefChipMolecule
              key={id}
              sphereId={id}
              readOnly={readOnly}
              onRemove={() => removeSphere(id)}
              className="relationship-value-editor-molecule__sphere-chip"
            />
          ))
        )}
        {!readOnly && (
          <>
            {!filter.relatedTo.includes('@me') && (
              <button
                type="button"
                className="relationship-value-editor-molecule__me-btn"
                onClick={addMe}
                data-testid={`relationship-add-me-${index}`}
              >
                <i className="pi pi-at" aria-hidden="true" />
                {t('context.editor.relationshipFilters.relatedToMe')}
              </button>
            )}
            <button
              type="button"
              className="relationship-value-editor-molecule__choose-btn"
              onClick={() => setPickerOpen(true)}
              data-testid={`relationship-choose-spheres-${index}`}
            >
              <i className="pi pi-plus" aria-hidden="true" />
              {t('context.editor.relationshipFilters.chooseSpheres')}
            </button>
          </>
        )}
      </div>

      {expanded && showMinCount && (
        <div className="relationship-value-editor-molecule__mincount">
          <span className="relationship-value-editor-molecule__mincount-label">
            {t('context.editor.relationshipFilters.atLeast')}
          </span>
          <InputNumberAtom
            value={filter.minCount ?? 1}
            onChange={(v) => update({ minCount: v ?? 1 })}
            min={1}
            type="integer"
            disabled={readOnly}
            className="relationship-value-editor-molecule__mincount-input"
          />
        </div>
      )}

      {pickerOpen && (
        <SphereSelectDialogOrganism
          open
          onClose={() => setPickerOpen(false)}
          titleKey="context.editor.relationshipFilters.pickerTitle"
          onSelect={addSphere}
          excludedSphereIds={filter.relatedTo.filter((id) => !isFilterVariable(id))}
          fixedContextFilters={relationship?.targetFilter?.propertyFilters ?? []}
          permission="view"
          showContextSelector={false}
        />
      )}
    </div>
  );
};

export default RelationshipValueEditorMolecule;
